import React, { useState, useEffect } from 'react';
import { Card, Alert, Table, Badge, Spinner, Button, Row, Col } from 'react-bootstrap';
import { get_json, post_json } from './util.js';
import { formatDistanceToNow } from 'date-fns';

function Server() {
    const [serverState, setServerState] = useState(null);
    const [serverInfo, setServerInfo] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchServerState = async () => {
        get_json('server/service-status', (response) => {
            if (response.error) {
                setError(response.error);
                return;
            }
            setServerState(response.state);
            setError(null);
            setLoading(false);
        });
    };

    const fetchServerInfo = async () => {
        get_json('server/status', (response) => {
            if (response.error) {
                setError(response.error);
                return;
            }
            setServerInfo(response);
            setError(null);
            setLoading(false);
        });
    };

    const handleRestart = async () => {
        get_json('svc/reload_update_service', (response) => {
            if (response.error) {
                setError(response.error);
                return;
            }
            setError(null);
        });
    };

    useEffect(() => {
        fetchServerState();
        fetchServerInfo();
        const interval = setInterval(() => {
            fetchServerState();
            fetchServerInfo();
        }, 5000); // Update every 5 seconds
        return () => clearInterval(interval);
    }, []);

    if (loading && !serverState && !serverInfo) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger" className="m-3">{error}</Alert>;
    }

    return (
        <div className="container-fluid px-4">
            <h2 className="mb-4">Server Status</h2>

            <div className="row">
                <div className="col-12">
                    {/* Database Info */}
                    {serverInfo && serverInfo.database && (
                        <Card className="mb-4">
                            <Card.Header>
                                <span className="text-uppercase fw-bold">Database</span>
                            </Card.Header>
                            <Card.Body>
                                <div><strong>Path:</strong> {serverInfo.database.path}</div>
                                <div><strong>Size:</strong> {serverInfo.database.size}</div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Feed Update Service */}
                    <Card className="mb-4">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-uppercase fw-bold">Feed Update Service</span>
                                <Button 
                                    variant="primary"
                                    onClick={handleRestart}
                                    size="sm"
                                >
                                    Restart Service
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : serverState && (
                                <Row className="g-3">
                                    <Col xs={12} sm={6} md={4}>
                                        <div className="mb-3">
                                            <div className="text-muted small">
                                                Status
                                            </div>
                                            <div className={serverState.is_polling ? 'text-success' : 'text-warning'}>
                                                {serverState.is_polling ? 'Active' : 'Inactive'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={4}>
                                        <div className="mb-3">
                                            <div className="text-muted small">
                                                Uptime
                                            </div>
                                            <div>
                                                {serverState.start_time ? 
                                                    formatDistanceToNow(new Date(serverState.start_time), { addSuffix: true }) :
                                                    'Not started'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={4}>
                                        <div className="mb-3">
                                            <div className="text-muted small">
                                                Active Feeds
                                            </div>
                                            <div>
                                                {serverState.feed_count}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="text-muted small mb-2">
                                            Feed Status
                                        </div>
                                        {Object.entries(serverState.last_update_status || {}).map(([feedId, status]) => (
                                            <div 
                                                key={feedId}
                                                className={`alert ${status.startsWith('Error') ? 'alert-danger' : 'alert-success'} mb-2 py-2`}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>{feedId}:</strong> {status}
                                                    </div>
                                                    {serverState.last_update_attempt[feedId] && (
                                                        <small className="text-muted">
                                                            {formatDistanceToNow(new Date(serverState.last_update_attempt[feedId]), { addSuffix: true })}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Sources Info */}
                    {serverInfo && serverInfo.sources && serverInfo.sources.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header>
                                <span className="text-uppercase fw-bold">Sources</span>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Entries</th>
                                            <th>Last Update</th>
                                            <th>Next Update</th>
                                            <th>Time Until Next</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serverInfo.sources.map((source, index) => (
                                            <tr key={index}>
                                                <td>{source.name}</td>
                                                <td>
                                                    <Badge bg={source.enabled ? 'success' : 'warning'}>
                                                        {source.enabled ? 'Enabled' : 'Disabled'}
                                                    </Badge>
                                                </td>
                                                <td>{source.entry_count}</td>
                                                <td>{source.last_update ? formatDistanceToNow(new Date(source.last_update), { addSuffix: true }) : 'Never'}</td>
                                                <td>{source.next_update ? formatDistanceToNow(new Date(source.next_update), { addSuffix: true }) : 'N/A'}</td>
                                                <td>{source.time_until_next}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Filters Info */}
                    {serverInfo && serverInfo.filters && serverInfo.filters.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header>
                                <span className="text-uppercase fw-bold">Filters</span>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Source</th>
                                            <th>Matches</th>
                                            <th>Last Match</th>
                                            <th>Mute Info</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serverInfo.filters.map((filter, index) => (
                                            <tr key={index}>
                                                <td>{filter.name}</td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        <Badge bg={filter.enabled ? 'success' : 'warning'}>
                                                            {filter.enabled ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                        {filter.muted ? (
                                                            <Badge bg="info">
                                                                Muted {filter.remaining_mute_time ? `(${filter.remaining_mute_time} remaining)` : ''}
                                                            </Badge>
                                                        ) : filter.mute_on_match && (
                                                            <Badge bg="secondary">
                                                                Auto-mute {filter.unmute_after}
                                                            </Badge>
                                                        )}
                                                        {filter.require_unique_matches && (
                                                            <Badge bg="primary">Unique matches</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{filter.source}</td>
                                                <td>{filter.match_count}</td>
                                                <td>{filter.last_match ? formatDistanceToNow(new Date(filter.last_match), { addSuffix: true }) : 'Never'}</td>
                                                <td>
                                                    {filter.muted && filter.mute_end_time && (
                                                        <div>Unmutes {formatDistanceToNow(new Date(filter.mute_end_time), { addSuffix: true })}</div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Recent Matches */}
                    {serverInfo && serverInfo.recent_matches && serverInfo.recent_matches.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header>
                                <span className="text-uppercase fw-bold">Recent Matches</span>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Filter</th>
                                            <th>Entry Title</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serverInfo.recent_matches.map((match, index) => (
                                            <tr key={index}>
                                                <td>{match.filter_name}</td>
                                                <td>{match.entry_title}</td>
                                                <td>{formatDistanceToNow(new Date(match.timestamp), { addSuffix: true })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Server; 